import { useRef, useEffect } from "react";
import DOMPurify from "isomorphic-dompurify";

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'div', 'ol', 'ul', 'li'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
};

// Strip invisible Unicode characters that come from pasted content
const stripInvisibleChars = (text) =>
  text
    .replace(/[\u200B\u200C\u200D\u200E\u200F]/g, '')   // zero-width chars
    .replace(/[\uFEFF]/g, '')                             // BOM
    .replace(/[\u00AD]/g, '')                             // soft hyphen
    .replace(/[\u2028\u2029]/g, '\n')                     // line/paragraph separators → newline
    .replace(/\u00A0/g, ' ');                             // non-breaking space → regular space

const sanitizeHtml = (html) => {
  let clean = DOMPurify.sanitize(html, SANITIZE_CONFIG);
  clean = stripInvisibleChars(clean);
  clean = clean.replace(/<a\s/g, '<a target="_blank" rel="noopener noreferrer" ');
  return clean;
};

export const RichTextEditor = ({ value, onChange, placeholder, testId, initialHtml }) => {
  const editorRef = useRef(null);
  const initializedRef = useRef(false);

  // Set initial HTML content once (for edit mode)
  useEffect(() => {
    if (initialHtml && editorRef.current && !initializedRef.current) {
      editorRef.current.innerHTML = sanitizeHtml(initialHtml);
      initializedRef.current = true;
    }
  }, [initialHtml]);

  const emitChange = () => {
    if (editorRef.current) {
      const raw = editorRef.current.innerHTML;
      onChange(sanitizeHtml(raw));
    }
  };

  const execCmd = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    emitChange();
  };

  // Intercept paste: prefer HTML for formatted content, strip invisible chars, sanitize
  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const html = clipboardData.getData('text/html');
    if (html) {
      // Sanitize pasted HTML to keep formatting but remove unsafe content
      const clean = sanitizeHtml(html);
      document.execCommand('insertHTML', false, clean);
    } else {
      // Fallback to plain text
      let text = clipboardData.getData('text/plain') || '';
      text = stripInvisibleChars(text);
      // If plain text contains HTML-like tags, strip them
      text = text.replace(/<[^>]*>/g, '');
      document.execCommand('insertText', false, text);
    }
    emitChange();
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50 flex-wrap">
        <button type="button" onClick={() => execCmd('bold')} className="p-1.5 rounded hover:bg-gray-200 font-bold text-sm" title="Bold">B</button>
        <button type="button" onClick={() => execCmd('italic')} className="p-1.5 rounded hover:bg-gray-200 italic text-sm" title="Italic">I</button>
        <button type="button" onClick={() => execCmd('underline')} className="p-1.5 rounded hover:bg-gray-200 underline text-sm" title="Underline">U</button>
        <button type="button" onClick={() => execCmd('strikeThrough')} className="p-1.5 rounded hover:bg-gray-200 line-through text-sm" title="Strikethrough">S</button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button type="button" onClick={() => execCmd('insertUnorderedList')} className="p-1.5 rounded hover:bg-gray-200 text-sm" title="Bullet list">• List</button>
        <button type="button" onClick={() => execCmd('insertOrderedList')} className="p-1.5 rounded hover:bg-gray-200 text-sm" title="Numbered list">1. List</button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button type="button" onClick={() => { const url = prompt('Enter URL:'); if (url) execCmd('createLink', url); }} className="p-1.5 rounded hover:bg-gray-200 text-sm text-blue-600" title="Insert link">Link</button>
        <button type="button" onClick={() => execCmd('removeFormat')} className="p-1.5 rounded hover:bg-gray-200 text-sm text-gray-400" title="Clear formatting">Clear</button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        data-testid={testId}
        className="min-h-[120px] max-h-[300px] overflow-y-auto px-3 py-2 text-sm focus:outline-none"
        data-placeholder={placeholder}
        onInput={emitChange}
        onPaste={handlePaste}
        suppressContentEditableWarning
        style={{ wordBreak: 'break-word', ...((!value || value === '<br>') ? { color: '#9ca3af' } : {}) }}
        onFocus={(e) => { if (e.target.innerHTML === '' || e.target.innerHTML === placeholder) { e.target.innerHTML = ''; e.target.style.color = ''; } }}
        onBlur={(e) => { if (!e.target.innerHTML.trim() || e.target.innerHTML === '<br>') onChange(''); }}
      />
    </div>
  );
};
